import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { performLogout } from './LogoutUtility';

/**
 * Selects the appropriate account from multiple MSAL accounts, prioritizing SignIn userflows.
 * If no SignIn account is found, logs out all accounts and redirects to sign-in with error details.
 *
 * @param instance - MSAL instance containing the accounts
 * @returns The selected account with SignIn userflow, or triggers logout if none found
 */
export function selectSignInAccount(
  instance: IPublicClientApplication
): AccountInfo | null {
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  // If only one account, use it
  if (accounts.length === 1) {
    return accounts[0];
  }

  // Multiple accounts - look for one with SignIn in the userflow
  const signInAccount = accounts.find((account) => {
    const tfp = (account.idTokenClaims as any)?.tfp;
    const acr = (account.idTokenClaims as any)?.acr;
    const userFlow = tfp || acr || '';

    // Check if the userflow contains 'SignIn' (case-insensitive)
    return userFlow.toLowerCase().includes('signin');
  });

  if (signInAccount) {
    return signInAccount;
  }

  // No SignIn account found with multiple accounts - this is an error state

  // Clear all accounts and redirect to sign-in with error details
  const errorParams = new URLSearchParams({
    error: 'no_signin_account',
    detail: 'Multiple_accounts_found_but_none_with_SignIn_userflow',
  });

  void performLogout(instance, `/sign-in?${errorParams.toString()}`);

  // Return null as we're logging out
  return null;
}

/**
 * Gets the current user account, prioritizing SignIn userflows.
 * This is a convenience wrapper around selectSignInAccount for hooks.
 *
 * @param instance - MSAL instance containing the accounts
 * @returns The selected account or undefined if none available
 */
export function getCurrentUserAccount(
  instance: IPublicClientApplication
): AccountInfo | undefined {
  const account = selectSignInAccount(instance);
  return account || undefined;
}
